import { pick } from 'lodash';
import { useFormErrors } from 'vee-validate';

export const wizardInjectionKey = Symbol('__use_Wizard') as unknown as InjectionKey<{
    currentIndex: Readonly<Ref<number>>;
    lastStepIndex: Readonly<Ref<number>>;
    next: () => void;
    prev: () => void;
    registerStep: (index: number, fields: Readonly<Ref<string[]>>) => void;
    unregisterStep: (index: number) => void;
    stepErrors: Readonly<Ref<Record<number, Partial<Record<string, string>>>>>;
}>;

export const useWizard = (
    onSubmit: () => void,
    options?: {
        /**
         * The lowest step the wizard may sit on. Defaults to 0.
         *
         * Reactive, because a step can stop being available part-way through: a wizard whose first
         * step performs a one-time action — setting a password that the API then refuses to set
         * again — must not offer Back to it once it is done. Raising this is how that is expressed,
         * rather than each caller hiding its own Back button and hoping nothing else moves the
         * index.
         */
        startAt?: MaybeRefOrGetter<number>;
    },
) => {
    const firstStepIndex = computed(() => toValue(options?.startAt) ?? 0);
    const currentIndex = ref(firstStepIndex.value);
    const registeredSteps = shallowRef<{ index: number; fields: Readonly<Ref<string[]>> }[]>([]);
    const lastStepIndex = computed(() => Math.max(...registeredSteps.value.map(step => step.index)));

    /**
     * Keeps the invariant when the floor rises under a wizard already sitting below it. Without
     * this the step stays rendered after the thing it was asking for has been done.
     */
    watch(firstStepIndex, first => {
        if (currentIndex.value < first) {
            currentIndex.value = first;
        }
    });

    const nextStep = () => {
        if (currentIndex.value < lastStepIndex.value) {
            currentIndex.value++;
        } else {
            onSubmit();
        }
    };

    const prevStep = () => {
        if (currentIndex.value > firstStepIndex.value) {
            currentIndex.value--;
        }
    };

    const registerStep = (index: number, fields: Readonly<Ref<string[]>>) => {
        registeredSteps.value = [...registeredSteps.value, { index, fields }];
    };

    const unregisterStep = (index: number) => {
        registeredSteps.value = registeredSteps.value.filter(step => step.index !== index);
    };

    const formErrors = useFormErrors();
    const stepErrors = computed(() => {
        const errors: Record<number, Partial<Record<string, string>>> = {};
        for (const step of registeredSteps.value) {
            errors[step.index] = pick(formErrors.value, step.fields.value);
        }
        return errors;
    });

    provide(wizardInjectionKey, {
        next: nextStep,
        prev: prevStep,
        currentIndex,
        registerStep,
        unregisterStep,
        lastStepIndex,
        stepErrors,
    });

    return {
        nextStep,
        prevStep,
        currentIndex,
        lastStepIndex,
        firstStepIndex,
    };
};
